import { currencyMap, languages, NULL_METRIC_SIGN } from '@app/configurations';
import { Currency, CurrencyRates } from '@app/core/models/customer.models';
import { microsToNumber, numberToMicros } from '@app/core/utilities/number.utilities';
import { getDelayRefinerKey } from '@app/core/utilities/refiners.utilities';
import { camelToSnake } from '@app/core/utilities/string.utilities';
import { MetricUIType } from '@app/shared/pipes/format-metric.pipe';
import {
  CampaignOptimizationValue,
  Goal,
  GoalPayload,
  MetricType,
  Micros,
  SubFilterTypes,
} from '../models/common.models';
import {
  AllGoalTypes,
  AllMetrics,
  AllMetricsArray,
  BUDGET_AMOUNT,
  CLICK_DAY,
  CLICK_THROUGH_RATE,
  CLICK_THROUGH_RATE_CLICKS,
  CLICKS,
  CONVERSION_CLICKS_DAY,
  CONVERSION_DAY,
  CONVERSION_RATE,
  CONVERSION_RATE_ADJUSTED,
  CONVERSION_RATE_CLICKS,
  CONVERSIONS,
  CONVERSIONS_ADJUSTED,
  CONVERSIONS_CLICKS,
  COST,
  COST_DAY,
  COST_FOR_CLICK,
  COST_FOR_PROFIT,
  COST_FOR_REVENUE,
  COST_FOR_VCPM,
  COST_PER_CLICK,
  COST_PER_CONVERSION,
  COST_PER_CONVERSION_ADJUSTED,
  COST_PER_CONVERSION_CLICKS,
  COST_PER_MILLE,
  COST_REVENUE_RATIO,
  COST_REVENUE_RATIO_ADJUSTED,
  COST_REVENUE_RATIO_CLICKS,
  COST_REVENUE_TOTAL_RATIO,
  COST_REVENUE_TOTAL_RATIO_ROLLING_AVG,
  costType,
  currencyMetrics,
  IMPRESSION_DAY,
  IMPRESSIONS,
  IMPRESSIONS_TOTAL,
  MAX_BID,
  MetricsWithFullNumber,
  MIN_BID,
  numberMetrics,
  percentMetrics,
  PG_GOAL_METRIC_COST_PER_CLICK,
  PG_GOAL_METRIC_COST_PER_CONVERSION,
  PG_GOAL_METRIC_COST_PER_CONVERSION_CLICKS,
  PG_GOAL_METRIC_COST_PER_DAY,
  PG_GOAL_METRIC_CRR,
  PG_GOAL_METRIC_CRR_CLICKS,
  PG_GOAL_METRIC_PROFIT_MAXIMIZATION,
  PG_GOAL_METRIC_ROAS,
  PG_GOAL_METRIC_ROAS_CLICKS,
  PG_GOAL_METRIC_ROI,
  PRICE,
  PROFIT,
  PROFIT_DAY,
  PROFIT_GROSS,
  PROFIT_GROSS_ADJUSTED,
  PROFIT_NET,
  PROFIT_NET_ADJUSTED,
  RETURN_ON_INVESTMENT,
  RETURN_ON_INVESTMENT_ADJUSTED,
  REVENUE,
  REVENUE_ADJUSTED,
  REVENUE_CLICKS,
  REVENUE_CLICKS_DAY,
  REVENUE_DAY,
  REVENUE_ORGANIC,
  REVENUE_ORGANIC_ADJUSTED,
  REVENUE_ORGANIC_ROLLING_AVG,
  REVENUE_TOTAL,
  REVENUE_TOTAL_ROLLING_AVG,
  ROAS,
  ROAS_ADJUSTED,
  ROAS_CLICKS,
  VIEW_IMPRESSIONS,
} from '../models/metrics.constants';
import { getCurrencyFromSymbolOrValue } from './utilities.service';

export interface IFormatMetricWithCurrency {
  raw: null | string;
  pretty: null | string;
  prettyFull: null | string;
}
export interface FormattingOptionsForMetrics {
  isValueInMicros?: boolean;
  isValueInPercent?: boolean;
  removeZeros?: boolean;
  convertToSnakeCase?: boolean;
  metricValueFullShort?: boolean;
}

export const goalToMetricMapping = {
  [PG_GOAL_METRIC_COST_PER_DAY]: COST,
  [PG_GOAL_METRIC_COST_PER_CLICK]: COST_PER_CLICK,
  [PG_GOAL_METRIC_COST_PER_CONVERSION]: COST_PER_CONVERSION,
  [PG_GOAL_METRIC_CRR]: COST_REVENUE_RATIO,
  [PG_GOAL_METRIC_CRR_CLICKS]: COST_REVENUE_RATIO_CLICKS,
  [PG_GOAL_METRIC_COST_PER_CONVERSION_CLICKS]: COST_PER_CONVERSION_CLICKS,
  [PG_GOAL_METRIC_ROI]: RETURN_ON_INVESTMENT,
  [PG_GOAL_METRIC_ROAS]: ROAS,
  [PG_GOAL_METRIC_ROAS_CLICKS]: ROAS_CLICKS,
  [PG_GOAL_METRIC_PROFIT_MAXIMIZATION]: PROFIT_GROSS,
};

/* #region format transformation functions */
export const prettifyNumbers = (amount: any, currency: string, decimalCount: number) => {
  if (isNaN(amount)) return NULL_METRIC_SIGN;
  const decimal = isGermanLocale() ? ',' : '.';
  const thousands = isGermanLocale() ? '.' : ',';
  try {
    const symbol = currencyMap[currency]?.symbol;

    decimalCount = Math.abs(decimalCount);
    decimalCount = isNaN(decimalCount) ? 2 : decimalCount;
    // decimalCount = amount >= 100 ? 0 : decimalCount;

    const negativeSign = amount < 0 ? '-' : '';

    const i = parseInt((amount = Math.abs(Number(amount) || 0).toFixed(decimalCount)), 0).toString();
    const j = i.length > 3 ? i.length % 3 : 0;
    if (!symbol) {
      return (
        negativeSign +
        (j ? i.substr(0, j) + thousands : '') +
        i.substr(j).replace(/(\d{3})(?=\d)/g, '$1' + thousands) +
        (decimalCount
          ? decimal +
            Math.abs(amount - parseInt(i, 0))
              .toFixed(decimalCount)
              .slice(2)
          : '')
      );
    }
    return (
      symbol +
      '' +
      negativeSign +
      (j ? i.substr(0, j) + thousands : '') +
      i.substr(j).replace(/(\d{3})(?=\d)/g, '$1' + thousands) +
      (decimalCount
        ? decimal +
          Math.abs(amount - parseInt(i, 0))
            .toFixed(decimalCount)
            .slice(2)
        : '')
    );
  } catch (e) {
    console.log(e);
  }
};
export const isCurrencyMetric = (metric: AllMetrics): boolean => {
  return currencyMetrics.includes(metric);
};
export const isMonetaryValue = isCurrencyMetric; // remove later;
export const isPercentMetric = (metric: AllMetrics): boolean => {
  return percentMetrics.includes(metric);
};
export const convertMetricNumberToMicros = (metric: AllMetrics, value: number) => {
  if (currencyMetrics.includes(metric)) {
    return numberToMicros(value);
  }
  return value;
};
export const convertMetricMicrosToNumber = (metric: AllMetrics, value: number) => {
  if (currencyMetrics.includes(metric)) {
    return microsToNumber(value);
  }
  return value;
};

export const convertGoalsToNumber = (metric: AllGoalTypes, value: number) => {
  if (currencyMetrics.includes(metric)) {
    return microsToNumber(value);
  } else if (percentMetrics.includes(metric)) {
    return 100 * value;
  }
  return value;
};

export const convertNumberToGoalValues = (metric: AllGoalTypes, value: number) => {
  if (currencyMetrics.includes(metric)) {
    return numberToMicros(value);
  } else if (percentMetrics.includes(metric)) {
    return value / 100;
  }
  return value;
};

export const hasThousandSeparator = (value): boolean => {
  if (!value) return false;
  return !!'BMK'.split('').filter((char) => value.includes(char)).length;
};

/* #region metrics core formulae */
export const getClickThroughRate = (clicks: number, impressions: number, raw = true): string | number => {
  if (!impressions) return NULL_METRIC_SIGN;

  if (!clicks) return '0';

  const formula = clicks / impressions;
  return raw ? formula : prettifyClickThroughRate(formula);
};
export const getConversionRate = (conversions: number, clicks: number, raw = true): string | number => {
  if (!clicks) return NULL_METRIC_SIGN;

  if (!conversions) return '0';

  const formula = conversions / clicks;
  return raw ? formula : prettifyConversionRate(formula);
};
export const getProductsReturnOnInvestment = (revenue: any, cost: number, raw = true): string | number => {
  if (!cost) return NULL_METRIC_SIGN;

  if (!revenue) return '0';

  const formula = revenue / cost;
  return raw ? formula : prettifyReturnOnInvestment(formula);
};
export const getReturnOnInvestment = (profit: any, cost: number, raw = true): string | number => {
  if (!cost) return NULL_METRIC_SIGN;

  if (!profit) return '0';

  const formula = profit / cost;
  return raw ? formula : prettifyReturnOnInvestment(formula);
};

export const getReturnOnAdSpend = (revenue: any, cost: number, raw = true): string | number => {
  if (!cost) return NULL_METRIC_SIGN;

  if (!revenue) return '0';

  const formula = revenue / cost;
  return raw ? formula : prettifyReturnOnInvestment(formula);
};
export const getCostPerConversion = (
  cost: number,
  conversions: number,
  raw = true,
  currency = null,
): string | number => {
  if (!conversions) return NULL_METRIC_SIGN;

  if (!cost) return '0';

  const formula = cost / conversions;
  return raw ? formula : prettifyNumbers(formula, currency, 2);
};
export const getEffectiveCPC = (cost: number, clicks: number, raw = true, currency = null): string | number => {
  if (!clicks) return NULL_METRIC_SIGN;

  if (!cost) return '0';

  const formula = cost / clicks;
  return raw ? formula : prettifyNumbers(formula, currency, 2);
};
export const getEffectiveCPM = (
  cost: number,
  viewImpressions: number,
  raw = true,
  currency = null,
): string | number => {
  if (!viewImpressions) return NULL_METRIC_SIGN;

  if (!cost) return '0';

  const formula = (1000 * cost) / viewImpressions;
  return raw ? formula : prettifyNumbers(formula, currency, 2);
};
export const getACoS = (cost: number, revenue: any, raw = true): string | number => {
  if (!revenue) return NULL_METRIC_SIGN;

  if (!cost) return '0';

  const formula = cost / revenue;
  return raw ? formula : prettifyACOS(formula);
};
export const getRevenueOrganic = (
  revenue_total: number,
  revenue: any,
  raw = true,
  currency = null,
): string | number => {
  if (!revenue) return NULL_METRIC_SIGN;

  const formula = revenue_total - revenue;
  return raw ? formula : prettifyReturnOnInvestment(formula, currency);
};
export const getCostRevenueRatio = getACoS;
/* #endregion */

/* #region aggregate metrics */
export const getMetricSummation = (data, key) => {
  switch (true) {
    case [
      CLICKS,
      CONVERSIONS,
      CONVERSIONS_CLICKS,
      COST,
      COST_FOR_CLICK,
      COST_FOR_REVENUE,
      COST_FOR_PROFIT,
      IMPRESSIONS,
      VIEW_IMPRESSIONS,
      COST_PER_MILLE,
      PROFIT_NET,
      PROFIT_NET_ADJUSTED,
      PROFIT_GROSS,
      PROFIT_GROSS_ADJUSTED,
      PRICE,
      CONVERSIONS_ADJUSTED,
      REVENUE,
      REVENUE_ADJUSTED,
      REVENUE_TOTAL,
      REVENUE_TOTAL_ROLLING_AVG,
      REVENUE_ORGANIC,
      REVENUE_ORGANIC_ADJUSTED,
      REVENUE_ORGANIC_ROLLING_AVG,
      REVENUE_CLICKS,
    ].includes(key):
      const hasAllNulls = data.filter((d) => d[key] === null).length === data.length;
      return hasAllNulls ? null : data.reduce((a, b) => a + (b[key] || 0), 0);
    case [COST_REVENUE_RATIO, COST_REVENUE_RATIO_ADJUSTED].includes(key):
      return getACoS(
        data.reduce((a, b) => a + (b.cost || 0), 0),
        data.reduce((a, b) => a + (b.revenue || 0), 0),
        true,
      );
    case [COST_REVENUE_RATIO_CLICKS].includes(key):
      return getACoS(
        data.reduce((a, b) => a + (b.cost || 0), 0),
        data.reduce((a, b) => a + (b.revenue_clicks || 0), 0),
        true,
      );
    case key === COST_REVENUE_TOTAL_RATIO || key === COST_REVENUE_TOTAL_RATIO_ROLLING_AVG:
      return getACoS(
        data.reduce((a, b) => a + (b.cost || 0), 0),
        data.reduce((a, b) => a + (b.revenue_total || 0), 0),
        true,
      );
    case [COST_PER_CONVERSION, COST_PER_CONVERSION_ADJUSTED].includes(key):
      return getCostPerConversion(
        data.reduce((a, b) => a + (b.cost || 0), 0),
        data.reduce((a, b) => a + (b.conversions || 0), 0),
        true,
      );
    case [COST_PER_CONVERSION_CLICKS].includes(key):
      return getCostPerConversion(
        data.reduce((a, b) => a + (b.cost || 0), 0),
        data.reduce((a, b) => a + (b.conversions_clicks || 0), 0),
        true,
      );
    case [CONVERSION_RATE, CONVERSION_RATE_ADJUSTED].includes(key):
      return getConversionRate(
        data.reduce((a, b) => a + (b.conversions || 0), 0),
        data.reduce((a, b) => a + (b.clicks || 0), 0),
        true,
      );
    case [CONVERSION_RATE_CLICKS].includes(key):
      return getConversionRate(
        data.reduce((a, b) => a + (b.conversions_clicks || 0), 0),
        data.reduce((a, b) => a + (b.clicks || 0), 0),
        true,
      );
    case [CLICK_THROUGH_RATE].includes(key):
      return getClickThroughRate(
        data.reduce((a, b) => a + (b.clicks || 0), 0),
        data.reduce((a, b) => a + (b.impressions_total || 0), 0),
        true,
      );
    case [CLICK_THROUGH_RATE_CLICKS].includes(key):
      return getClickThroughRate(
        data.reduce((a, b) => a + (b.clicks || 0), 0),
        data.reduce((a, b) => a + (b.impressions || 0), 0),
        true,
      );
    case key === COST_PER_CLICK:
      return getEffectiveCPC(
        data.reduce((a, b) => a + (b.cost || 0), 0),
        data.reduce((a, b) => a + (b.clicks || 0), 0),
        true,
      );
    case key === COST_PER_MILLE:
      return getEffectiveCPM(
        data.reduce((a, b) => a + (b.cost || 0), 0),
        data.reduce((a, b) => a + (b.view_impressions || 0), 0),
        true,
      );
    case [RETURN_ON_INVESTMENT, RETURN_ON_INVESTMENT_ADJUSTED].includes(key):
      return getReturnOnInvestment(
        data.reduce((a, b) => a + (b.revenue || 0), 0),
        data.reduce((a, b) => a + (b.cost || 0), 0),
        true,
      );
    case [ROAS_ADJUSTED, ROAS, ROAS_CLICKS].includes(key):
      return getReturnOnAdSpend(
        data.reduce((a, b) => a + (b.revenue || 0), 0),
        data.reduce((a, b) => a + (b.cost_for_revenue || 0), 0),
        true,
      );
  }
};
/* #endregion */

/* #region prettify metrics */
export const prettifyClickThroughRate = (value: number, currency = null) => {
  return value === null ? NULL_METRIC_SIGN : prettifyNumbers(value * 100, currency, 2) + '%';
};
export const prettifyConversionRate = (value: number, currency = null) => {
  return value === null ? NULL_METRIC_SIGN : prettifyNumbers(value * 100, currency, 2) + '%';
};
export const prettifyCostPerConversion = (value: Micros, currency = null) => {
  return value === null ? NULL_METRIC_SIGN : prettifyNumbers(microsToNumber(value), currency, 2);
};
export const prettifyEffectiveCPC = (value: Micros, currency = null) => {
  return value === null ? NULL_METRIC_SIGN : prettifyNumbers(microsToNumber(value), currency, 2);
};
export const prettifyACOS = (value: number, currency = null) => {
  return value === null ? NULL_METRIC_SIGN : prettifyNumbers(100 * value, currency, 2) + '%';
};
export const prettifyReturnOnInvestment = (value: number, currency = null) => {
  return value === null ? NULL_METRIC_SIGN : prettifyNumbers(value, currency, 2);
};
/* #endregion */
export const getLocale = () => {
  let locale = 'en-US';
  switch (window.adspert_data.user.lang) {
    case languages.GERMAN:
      locale = 'de-DE';
      break;
    case languages.CHINESE:
      locale = 'zh-CN';
      break;
  }
  return locale;
};

export const isGermanLocale = () => window.adspert_data.user.lang === languages.GERMAN;

export const intlFormat = (num, fractionDigits = 2) => {
  return new Intl.NumberFormat(getLocale(), {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(num);
};
export const formatNumber = (num, ignoreFormat = false, minimumFractionDigits = 2) => {
  if (isNaN(num) || !isFinite(num) || num === null) return NULL_METRIC_SIGN;

  if (typeof num === 'string') num = parseFloat(num);

  if (ignoreFormat) {
    return intlFormat(num, minimumFractionDigits);
  }
  if (Math.abs(num) >= 1000000000) {
    return intlFormat(num / 1000000000, minimumFractionDigits) + 'B';
  }
  if (Math.abs(num) >= 1000000) {
    return intlFormat(num / 1000000, minimumFractionDigits) + 'M';
  }
  if (Math.abs(num) >= 1000) {
    return intlFormat(num / 1000, minimumFractionDigits) + 'K';
  }
  if (Math.abs(num) >= 100) {
    return intlFormat(num, minimumFractionDigits);
  }
  return intlFormat(num, minimumFractionDigits);
};

export const formatPercent = (num, ignoreFormat = false, minimumFractionDigits = 2) => {
  if (isNaN(num) || !isFinite(num) || num === null) return NULL_METRIC_SIGN;

  return `${formatNumber(num * 100, ignoreFormat, minimumFractionDigits)}%`;
};

export const formatCurrency = (num, currency: Currency, ignoreFormat = false, minimumFractionDigits = 2) => {
  if (isNaN(num) || !isFinite(num) || num === null) return NULL_METRIC_SIGN;

  const symbol = currencyMap[currency]?.symbol ?? currency ?? '';

  return `${symbol}${formatNumber(num, ignoreFormat, minimumFractionDigits)}`;
};

export const formatMetric = (
  metric,
  value,
  currency?: Currency,
  ignoreFormat?: boolean,
): { raw: string; pretty: string; prettyFull: string } => {
  // tslint:disable-next-line: one-variable-per-declaration
  let pretty, t, raw, prettyFull;
  if (value == null || isNaN(value) || !isFinite(value) || metric === null || value === NULL_METRIC_SIGN)
    return { raw: null, pretty: NULL_METRIC_SIGN, prettyFull: NULL_METRIC_SIGN };

  const parsedValued = +value;
  if (numberMetrics.includes(metric)) {
    try {
      prettyFull = formatNumber(parsedValued, true, parsedValued < 1e3 ? 2 : 0);
    } catch (error) {
      prettyFull = NULL_METRIC_SIGN;
    }
    pretty = formatNumber(value, ignoreFormat == null || undefined ? false : ignoreFormat, parsedValued > 1e3 ? 2 : 0);
    t = pretty.slice(-1);
    raw = hasThousandSeparator(t) ? formatNumber(value, true, 0) : value;
    const decimal = isGermanLocale() ? ',00' : '.00';
    pretty = typeof pretty === 'string' ? pretty.replace(decimal, '') : pretty;
    prettyFull = typeof prettyFull === 'string' ? prettyFull.replace(decimal, '') : prettyFull;
  } else if (isMonetaryValue(metric)) {
    try {
      prettyFull = formatCurrency(parsedValued, currency, true, parsedValued < 1e3 ? 2 : 0);
    } catch (error) {
      prettyFull = NULL_METRIC_SIGN;
    }
    pretty = formatCurrency(value, currency, ignoreFormat == null || undefined ? false : ignoreFormat);
    t = pretty.slice(-1);
    raw = hasThousandSeparator(t) ? formatCurrency(value, currency, true) : value;
  } else if (percentMetrics.includes(metric)) {
    try {
      prettyFull = formatPercent(parsedValued?.toFixed(4), true, parsedValued < 1e3 ? 2 : 0);
    } catch (error) {
      prettyFull = NULL_METRIC_SIGN;
    }
    pretty = formatPercent(value, ignoreFormat == null || undefined ? false : ignoreFormat);
    t = pretty.slice(-1);
    raw = hasThousandSeparator(t) ? formatPercent(value, true) : typeof value === 'number' ? value * 100 : value;
  }

  raw = typeof raw === 'number' && !Number.isInteger(raw) ? (+raw.toFixed(3)).toFixed(2) : raw;

  return { raw, pretty, prettyFull };
};

export const MetricValueFullShort = (value: MetricUIType, key: AllMetrics) => {
  if (MetricsWithFullNumber.includes(key)) return value?.prettyFull;
  return value?.pretty;
};

export const getMetricType = (key): MetricType | false => {
  if (numberMetrics.includes(key)) {
    return MetricType.number;
  }
  if (percentMetrics.includes(key)) {
    return MetricType.percent;
  }
  if (isMonetaryValue(key)) {
    return MetricType.currency;
  }
  return false;
};

export const formatGoalValue = (value, type, currency?: Currency, inMircos = true) => {
  if (!value) {
    return null;
  }
  switch (type) {
    case PG_GOAL_METRIC_COST_PER_CLICK:
    case PG_GOAL_METRIC_COST_PER_CONVERSION:
    case PG_GOAL_METRIC_COST_PER_DAY:
    case PG_GOAL_METRIC_PROFIT_MAXIMIZATION:
    case 'COST-PER-MONTH':
      return formatCurrency(inMircos ? microsToNumber(value) : value, currency, true);
    case PG_GOAL_METRIC_CRR:
      return formatPercent(value, true);
    default:
      return formatNumber(value, true);
  }
};

export const formatGoalsPayload = (goals: Goal[]): GoalPayload[] => {
  return goals.map((goal) => {
    let value = parseFloat(goal.goalValue as any);
    let currency = null;
    switch (goal.goalType) {
      case PG_GOAL_METRIC_COST_PER_CLICK:
      case PG_GOAL_METRIC_COST_PER_CONVERSION:
      case PG_GOAL_METRIC_COST_PER_DAY:
        value = numberToMicros(value);
        currency = getCurrencyFromSymbolOrValue(goal.goalCurrency);
        break;
      case PG_GOAL_METRIC_PROFIT_MAXIMIZATION:
        value = null;
        break;
    }
    return {
      goal_type: goal.goalType,
      goal_value: value,
      goal_currency: currency,
    };
  });
};

export const summationByMetric = (data: Record<string, number>[], key: string, convertToMicrosToNumber = true) => {
  const hasAllNulls = data.filter((d) => d[key] === null).length === data.length;
  if (hasAllNulls) return null;
  const sum = data.reduce((a, b) => a + (b[key] || 0), 0);

  if (!convertToMicrosToNumber) return sum;
  return isMonetaryValue(<AllMetrics>key) ? microsToNumber(sum) : sum;
};

export function averageByMetric<T extends Record<AllMetrics, number>>(
  data: T[],
  key: AllMetrics,
  filterFunc?: (item: T) => boolean,
  convertToMicrosToNumber = true,
): number {
  data = data.filter((item) => item[key] != null && (!filterFunc || filterFunc(item)));

  if (!data.length) return null;

  return summationByMetric(data, key, convertToMicrosToNumber) / data.length;
}

export function convertMicrostoNumbers<T extends Record<string, any>>(data: Array<T> | T): any {
  function tmp(item) {
    const updatedItem = {};
    Object.keys(item).forEach((key: AllMetrics) => {
      if (isMonetaryValue(key) || (isMonetaryValue(<AllMetrics>camelToSnake(key)) && !isNaN(item[key])))
        updatedItem[key] = item[key] === null ? null : microsToNumber(item[key]);
      else updatedItem[key] = item[key];
    });
    return updatedItem as T;
  }
  if (data instanceof Array) return data.map((item: T) => tmp(item));
  return tmp(data);
}

export function RemoveDelaySuffix(metric: AllMetrics) {
  if (!metric) return metric;
  return <AllMetrics>metric?.replace('_adjusted', '');
}

export const RoundPayloadMetrics = (key, value, currencyValuesInMicros) => {
  if (value == null) return null;
  if (!AllMetricsArray.includes(key) || isNaN(value)) return value; //return same value for other fields eg., date, pg_name
  if (isMonetaryValue(key)) {
    if (!currencyValuesInMicros) return +value.toFixed(2);

    const val = +(value / 1e6).toFixed(2) * 1e6;
    return val / 1e6;
  } else if (percentMetrics.includes(key)) return +(value * 100).toFixed(2) / 100;

  return +value.toFixed(2);
};

export function HasDelaySuffix(metric: AllMetrics) {
  return !!metric?.endsWith('_adjusted');
}

export function TransformFiltersValuesForParentComponent(filters, optimizationType) {
  return filters.map((filter) => {
    let key: string = filter[0];
    let value: string = filter[1];
    const type: SubFilterTypes = filter[2];
    const metric = <AllMetrics>key.split('_').slice(1, -1).join('_');

    if (['filter_cost_ge', 'filter_cost_le'].includes(key))
      key = key.replace('cost', getCostFieldName(optimizationType));

    if (type === 'compare' && isMonetaryValue(metric))
      value = value
        .split('^^')
        .map((v) => numberToMicros(parseFloat(v)))
        .join('^^');

    return [key, value];
  });
}

export function FormatMetricWithCurrency(
  value: string | number,
  _metric: AllMetrics,
  from: Currency,
  to: Currency,
  rates: CurrencyRates,
  options: FormattingOptionsForMetrics = {},
) {
  let rate = 1;
  const {
    metricValueFullShort = false,
    removeZeros = false,
    convertToSnakeCase = false,
    isValueInMicros = true,
    isValueInPercent = false,
  } = options;

  const displayCurrency = !!to ? to : from;
  const metric = convertToSnakeCase ? <AllMetrics>camelToSnake(_metric) : _metric;

  if (metric === PG_GOAL_METRIC_PROFIT_MAXIMIZATION) {
    return { raw: null, pretty: null, prettyFull: null };
  }

  if ([MIN_BID, MAX_BID, BUDGET_AMOUNT].includes(metric) && value == null) {
    return { raw: null, pretty: NULL_METRIC_SIGN, prettyFull: NULL_METRIC_SIGN };
  }

  let tempValue = value != null && isValueInMicros && isMonetaryValue(metric) ? microsToNumber(+value) : value;

  if (!!to && to !== from && isMonetaryValue(metric)) {
    rate = rates[`${from}-${to}`] ?? 1;
  }

  if (isPercentMetric(metric) && isValueInPercent && !isNaN(+tempValue)) {
    // this is used only for percent metrics
    tempValue = +tempValue / 100;
  }

  let response = formatMetric(metric, +tempValue * rate, displayCurrency);

  if (removeZeros) response = removeTrailingZeros(response);

  if (metricValueFullShort) response.prettyFull = MetricValueFullShort(response, metric);

  return response;
}

export function removeTrailingZeros({ pretty, prettyFull, raw }) {
  const decimalSeparator = isGermanLocale() ? ',' : '.';
  const zeroPattern = `${decimalSeparator}00`;
  if (typeof pretty !== 'string') {
    pretty = pretty?.toString();
  }
  if (typeof prettyFull !== 'string') {
    prettyFull = prettyFull?.toString();
  }

  // Check if string ends with the zero pattern and replace with empty string
  if (pretty?.endsWith(zeroPattern)) {
    pretty = pretty.slice(0, -3);
  }
  if (prettyFull?.endsWith(zeroPattern)) {
    prettyFull = prettyFull.slice(0, -3);
  }
  return { pretty, prettyFull, raw };
}
// todo remove condition once cost_optimization_type_vcpm is added
export const getCostFieldName = (optType: string) =>
  optType === 'VCPM' ? 'cost' : `cost_for_${optType.toLowerCase()}`;

export const metricCalculations: Partial<
  Record<
    AllMetrics,
    (
      sums: Record<AllMetrics, number>,
      dataLength?: number,
      delay?: boolean,
      type?: CampaignOptimizationValue,
    ) => number | string
  >
> = {
  [COST]: (sums, _, __, type) => sums[costType[type]],
  [COST_DAY]: (sums, dataLength, _, type) => sums[costType[type]] / dataLength,
  [IMPRESSIONS]: (sums) => sums[IMPRESSIONS],
  [VIEW_IMPRESSIONS]: (sums) => sums[VIEW_IMPRESSIONS],
  [IMPRESSION_DAY]: (sums, dataLength) => sums[IMPRESSIONS] / dataLength,
  [CLICKS]: (sums) => sums[CLICKS],
  [CLICK_DAY]: (sums, dataLength) => sums[CLICKS] / dataLength,
  [COST_PER_CLICK]: (sums) => getEffectiveCPC(sums[COST], sums[CLICKS]),
  [COST_PER_MILLE]: (sums) => getEffectiveCPM(sums[COST_FOR_VCPM], sums[VIEW_IMPRESSIONS]),
  [CLICK_THROUGH_RATE]: (sums) => getClickThroughRate(sums[CLICKS], sums[IMPRESSIONS_TOTAL]),
  [CONVERSION_RATE]: (sums, _, delay) => getConversionRate(sums[getDelayRefinerKey(CONVERSIONS, delay)], sums[CLICKS]),
  [CONVERSIONS]: (sums, _, delay) => sums[getDelayRefinerKey(CONVERSIONS, delay)],
  [CONVERSION_DAY]: (sums, dataLength, delay) => sums[getDelayRefinerKey(CONVERSIONS, delay)] / dataLength,
  [COST_PER_CONVERSION]: (sums, _, delay) =>
    getCostPerConversion(sums[COST] - sums[COST_FOR_CLICK], sums[getDelayRefinerKey(CONVERSIONS, delay)]),
  [RETURN_ON_INVESTMENT]: (sums, _, delay) => sums[getDelayRefinerKey(PROFIT_GROSS, delay)] / sums[COST_FOR_PROFIT],
  [PROFIT]: (sums, _, delay) => sums[getDelayRefinerKey(PROFIT_GROSS, delay)],
  [PROFIT_GROSS]: (sums, _, delay) => sums[getDelayRefinerKey(PROFIT_GROSS, delay)],
  [PROFIT_DAY]: (sums, dataLength, delay) => sums[getDelayRefinerKey(PROFIT_GROSS, delay)] / dataLength,
  [COST_REVENUE_RATIO]: (sums, _, delay) =>
    getACoS(sums[COST] - sums[COST_FOR_CLICK], sums[getDelayRefinerKey(REVENUE, delay)]),
  [ROAS]: (sums) => getReturnOnAdSpend(sums[getDelayRefinerKey(REVENUE)], sums[COST_FOR_REVENUE]),
  [REVENUE]: (sums, _, delay) => sums[getDelayRefinerKey(REVENUE, delay)],
  [REVENUE_DAY]: (sums, dataLength, delay) => sums[getDelayRefinerKey(REVENUE, delay)] / dataLength,

  [REVENUE_CLICKS_DAY]: (sums, dataLength, delay) => sums[getDelayRefinerKey(REVENUE_CLICKS, delay)] / dataLength,
  [CONVERSION_CLICKS_DAY]: (sums, dataLength, delay) =>
    sums[getDelayRefinerKey(CONVERSIONS_CLICKS, delay)] / dataLength,
  [ROAS_CLICKS]: (sums) => getReturnOnAdSpend(sums[getDelayRefinerKey(REVENUE_CLICKS)], sums[COST_FOR_REVENUE]),
  [COST_REVENUE_RATIO_CLICKS]: (sums, _, delay) =>
    getACoS(sums[COST] - sums[COST_FOR_CLICK], sums[getDelayRefinerKey(REVENUE_CLICKS, delay)]),
  [COST_PER_CONVERSION_CLICKS]: (sums, _, delay) =>
    getCostPerConversion(sums[COST] - sums[COST_FOR_CLICK], sums[getDelayRefinerKey(CONVERSIONS_CLICKS, delay)]),
  [CONVERSIONS_CLICKS]: (sums, _, delay) => sums[getDelayRefinerKey(CONVERSIONS_CLICKS, delay)],
  [REVENUE_CLICKS]: (sums, _, delay) => sums[getDelayRefinerKey(REVENUE_CLICKS, delay)],
  [CLICK_THROUGH_RATE_CLICKS]: (sums) => getClickThroughRate(sums[CLICKS], sums[IMPRESSIONS]),
  [CONVERSION_RATE_CLICKS]: (sums, _, delay) =>
    getConversionRate(sums[getDelayRefinerKey(CONVERSIONS_CLICKS, delay)], sums[CLICKS]),
};

export function getRateFactor(
  metric: AllMetrics,
  pgCurrency: Currency,
  userCurrency: Currency,
  rates: CurrencyRates,
): number {
  if (!metric || !isCurrencyMetric(<AllMetrics>metric)) return 1;
  return rates[`${userCurrency}-${pgCurrency}`];
}
